import React, { useState } from 'react';
import { I18n } from 'react-redux-i18n';

import { I18N_BASE } from '../../constants';
import { DisagreementReason, NOTES_MAX_LENGTH } from '../../models';
import CmButton from '../../components/primitives/CmButton';
import CmRadioGroup from '../../components/primitives/CmRadioGroup';
import styles from './review.module.css';

const REASONS: readonly DisagreementReason[] = [
  'aiMissedRedFlag',
  'aiFalseSignal',
  'contextOutsidePage',
  'edgeCaseNotInRubric',
  'borderline',
];

export interface ApproveReasonPromptProps {
  open: boolean;
  onCancel: () => void;
  onSubmit: (data: { reason: DisagreementReason; notes: string | null }) => void;
  isSubmitting?: boolean;
  testId?: string;
}

export function ApproveReasonPrompt({
  open,
  onCancel,
  onSubmit,
  isSubmitting = false,
  testId,
}: ApproveReasonPromptProps): React.ReactElement | null {
  const [reason, setReason] = useState<DisagreementReason | null>(null);
  const [notes, setNotes] = useState('');

  if (!open) return null;

  const handleSubmit = () => {
    if (!reason) return;
    onSubmit({ reason, notes: notes.trim() ? notes.trim() : null });
  };

  const options = REASONS.map((value) => ({
    value,
    label: I18n.t(`${I18N_BASE}.disagreement.${value}`),
  }));

  return (
    <div className={styles.approvePrompt} data-testid={testId}>
      <div className={styles.fieldLabel}>
        {I18n.t(`${I18N_BASE}.approvePrompt.title`)}
      </div>
      <CmRadioGroup
        name="approveReason"
        value={reason}
        options={options}
        onChange={setReason}
        testId="approve-reason-radio"
      />
      <textarea
        placeholder={I18n.t(`${I18N_BASE}.approvePrompt.notesPlaceholder`)}
        value={notes}
        maxLength={NOTES_MAX_LENGTH}
        onChange={(e) => setNotes(e.target.value)}
        data-testid="approve-notes"
        rows={3}
      />
      <div className={styles.actionBarButtons} style={{ justifyContent: 'flex-end' }}>
        <CmButton variant="ghost" onClick={onCancel} testId="approve-cancel">
          {I18n.t(`${I18N_BASE}.approvePrompt.cancel`)}
        </CmButton>
        <CmButton
          variant="primary"
          onClick={handleSubmit}
          disabled={!reason || isSubmitting}
          loading={isSubmitting}
          testId="approve-confirm"
        >
          {I18n.t(`${I18N_BASE}.approvePrompt.confirm`)}
        </CmButton>
      </div>
    </div>
  );
}

export default ApproveReasonPrompt;
